import { useMemo } from 'react';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate } from '../../../utils/dateUtils';
import { buildMemberFamilyMap, resolveFamilyGroup, offeringOwner } from '../../../utils/familyReportGrouping';
import styles from '../MemberReports.module.css';

const offeringMemberName = (o) => o.member?.name || o.userId?.name || '—';
const categoryName       = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || '—';

export default function FamilyReport({ offerings, families, viewMode }) {
  /* ── Build userId → family map ──────────────────────────────────────── */
  const memberToFamily = useMemo(() => buildMemberFamilyMap(families), [families]);

  /* ── Group offerings by family ──────────────────────────────────────── */
  const familyGroups = useMemo(() => {
    const map = {};
    offerings.filter((o) => o.status !== 'cancelled').forEach((o) => {
      const uid = String(o.userId?.id ?? o.userId);
      const owner = offeringOwner(o);
      const { key, label } = resolveFamilyGroup(owner.id, owner.name, memberToFamily);
      if (!map[key]) map[key] = { label, members: {}, pledged: 0, paid: 0, pending: 0 };
      map[key].pledged += Number(o.offeredAmount) || 0;
      map[key].paid    += Number(o.paidAmount)    || 0;
      map[key].pending += Number(o.pendingAmount) || 0;

      const bucketId = uid;
      if (!map[key].members[bucketId]) {
        map[key].members[bucketId] = { name: offeringMemberName(o), entries: [], pledged: 0, paid: 0, pending: 0 };
      }

      map[key].members[bucketId].entries.push(o);
      map[key].members[bucketId].pledged += Number(o.offeredAmount) || 0;
      map[key].members[bucketId].paid    += Number(o.paidAmount)    || 0;
      map[key].members[bucketId].pending += Number(o.pendingAmount) || 0;
    });

    // Resolve head names (head may have no direct entries, name comes from member data)
    families.forEach((fam) => {
      const key = String(fam.id);
      if (!map[key]) return;
      (fam.members || []).forEach((m) => {
        if (m.member?.id && map[key].members[String(m.member.id)]) {
          map[key].members[String(m.member.id)].name = m.member.name;
        }
      });
    });

    return Object.values(map)
      .map((g) => ({
        ...g,
        members: Object.values(g.members).sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [offerings, memberToFamily, families]);

  const grandPledged = familyGroups.reduce((s, g) => s + g.pledged, 0);
  const grandPaid    = familyGroups.reduce((s, g) => s + g.paid,    0);
  const grandPending = familyGroups.reduce((s, g) => s + g.pending, 0);

  /* ── Summary view ───────────────────────────────────────────────────── */
  if (viewMode === 'summary') {
    return (
      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Family</th>
                <th style={{ width: 70, textAlign: 'center' }}>Members</th>
                <th className={styles.amtRight}>Total Offered (₹)</th>
                <th className={styles.amtRight}>Total Paid (₹)</th>
                <th className={styles.amtRight}>Total Pending (₹)</th>
              </tr>
            </thead>
            <tbody>
              {familyGroups.length === 0 && (
                <tr><td colSpan={6} className="empty-state">No family data found.</td></tr>
              )}
              {familyGroups.map((g, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><strong>{g.label}</strong></td>
                  <td style={{ textAlign: 'center' }}>{g.members.length}</td>
                  <td className={styles.amtRight}>{fmtCurrency(g.pledged)}</td>
                  <td className={styles.amtRight}>{fmtCurrency(g.paid)}</td>
                  <td className={`${styles.amtRight} ${g.pending > 0 ? styles.pending : ''}`}>
                    {fmtCurrency(g.pending)}
                  </td>
                </tr>
              ))}
            </tbody>
            {familyGroups.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>Grand Total ({familyGroups.length} famil{familyGroups.length !== 1 ? 'ies' : 'y'})</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(grandPledged)}</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(grandPaid)}</strong></td>
                  <td className={`${styles.amtRight} ${grandPending > 0 ? styles.pending : ''}`}>
                    <strong>{fmtCurrency(grandPending)}</strong>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  }

  /* ── Detail view ────────────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {familyGroups.length === 0 && (
        <div className="card"><div className="empty-state">No family data found.</div></div>
      )}

      {familyGroups.map((fam) => (
        <div className="card" key={fam.label}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>{fam.label}</h3>
            <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
              {fam.members.length} member{fam.members.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
              Offered {fmtCurrency(fam.pledged)} &nbsp;·&nbsp;
              Paid {fmtCurrency(fam.paid)} &nbsp;·&nbsp;
              Pending <span className={fam.pending > 0 ? styles.pending : ''}>{fmtCurrency(fam.pending)}</span>
            </span>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th className={styles.amtRight}>Offered (₹)</th>
                  <th className={styles.amtRight}>Paid (₹)</th>
                  <th className={styles.amtRight}>Pending (₹)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fam.members.map((mem) => {
                  const memOwnPledged = mem.entries.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0);
                  const memOwnPaid    = mem.entries.reduce((s, o) => s + (Number(o.paidAmount)    || 0), 0);
                  const memOwnPending = mem.entries.reduce((s, o) => s + (Number(o.pendingAmount) || 0), 0);
                  return (
                    <>
                      <tr key={`hdr-${mem.name}`} className={styles.memberGroupRow}>
                        <td colSpan={7}>{mem.name}</td>
                      </tr>
                      {mem.entries.map((o, i) => (
                        <tr key={o.id ?? i}>
                          <td style={{ paddingLeft: 20 }}>{i + 1}</td>
                          <td>{categoryName(o)}</td>
                          <td>{formatDate(o.offeringDate)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                          <td className={`${styles.amtRight} ${Number(o.pendingAmount) > 0 ? styles.pending : ''}`}>
                            {fmtCurrency(o.pendingAmount)}
                          </td>
                          <td>
                            <span className={`badge badge-${
                              o.status === 'completed' ? 'approved' :
                              o.status === 'partial'   ? 'submitted' :
                              o.status === 'cancelled' ? 'rejected' : 'pending'
                            }`}>{o.status}</span>
                          </td>
                        </tr>
                      ))}
                      {mem.entries.length > 0 && (
                        <tr key={`sub-mem-${mem.name}`} className={styles.memberSubtotalRow}>
                          <td colSpan={3} style={{ paddingLeft: 20 }}>
                            {mem.name} Subtotal — {mem.entries.length} entr{mem.entries.length !== 1 ? 'ies' : 'y'}
                          </td>
                          <td className={styles.amtRight}>{fmtCurrency(memOwnPledged)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(memOwnPaid)}</td>
                          <td className={`${styles.amtRight} ${memOwnPending > 0 ? styles.pending : ''}`}>
                            {fmtCurrency(memOwnPending)}
                          </td>
                          <td />
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>Family Total</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(fam.pledged)}</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(fam.paid)}</strong></td>
                  <td className={`${styles.amtRight} ${fam.pending > 0 ? styles.pending : ''}`}>
                    <strong>{fmtCurrency(fam.pending)}</strong>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
